import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { GithubMark } from './GithubConnect';
import {
  STARTER_SOURCES, badgeText, shuffled, sourceUrl, thumbnailUrl,
} from '../data/starterSources';

/**
 * A row of things worth turning into a skill.
 *
 * The composer above accepts a link, which is only useful once you have one in
 * mind — and "think of a good video" is real work to ask of someone who arrived
 * ten seconds ago. This answers it for them: a click fills the box and all that
 * is left is to send it.
 *
 * Deliberately mixed rather than grouped by kind. Two rows labelled Videos and
 * Repositories would read as two features; one row of things reads as the range
 * of what the product takes.
 */

// Pixels per second. Slow enough to read a title in passing, which is the only
// reason the row moves at all.
const DRIFT_PX_PER_SECOND = 22;
// After a manual scroll, arrow or drag, before the drift resumes.
const RESUME_AFTER_MS = 2600;

function Card({ item, onPick }) {
  const [failed, setFailed] = useState(false);
  const isVideo = item.kind === 'youtube';
  return (
    <button
      type="button"
      onClick={() => onPick(sourceUrl(item), item)}
      title={`Build a skill from ${item.title}`}
      className="group snap-start shrink-0 w-[12.5rem] sm:w-[15.5rem] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] rounded-lg"
    >
      <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-[var(--border-2)] bg-[var(--surface)] group-hover:border-[var(--accent)] transition-colors">
        {!failed ? (
          <img
            src={thumbnailUrl(item)}
            alt=""
            loading="lazy"
            onError={() => setFailed(true)}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
          />
        ) : (
          // A dead thumbnail must not leave a hole in the row.
          <div className="w-full h-full flex items-center justify-center text-[var(--text-4)]">
            {isVideo ? <Play size={20} /> : <GithubMark size={20} />}
          </div>
        )}

        <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-black/75 text-white text-[0.62rem] mono uppercase tracking-[0.1em]">
          {isVideo ? <Play size={9} fill="currentColor" /> : <GithubMark size={9} />}
          {isVideo ? 'Video' : 'Repo'}
        </span>
        <span className="absolute bottom-1.5 right-1.5 rounded px-1.5 py-0.5 bg-black/80 text-white text-[0.68rem] mono tracking-tight">
          {badgeText(item)}
        </span>

        {/* Says what the click does. These look like links out to YouTube, and
            this is the one moment to correct that. */}
        <span className="absolute inset-0 flex items-center justify-center bg-[var(--bg)]/70 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="mono text-[0.68rem] tracking-[0.14em] uppercase text-[var(--text)] border border-[var(--accent)] rounded-full px-3 py-1.5 bg-[var(--bg)]/80">
            Build a skill
          </span>
        </span>
      </div>

      <div className="mt-2 px-0.5">
        <div className="text-[0.88rem] leading-snug text-[var(--text-2)] group-hover:text-[var(--text)] transition-colors line-clamp-2">
          {item.title}
        </div>
        <div className="text-[0.76rem] text-[var(--text-4)] mt-0.5 truncate">{item.author}</div>
      </div>
    </button>
  );
}

export default function StarterGallery({ onPick }) {
  // Shuffled once per mount. CRA renders rather than hydrates, so a different
  // order from the prerendered snapshot costs nothing.
  const [items] = useState(() => shuffled(STARTER_SOURCES));
  const railRef = useRef(null);
  const pausedRef = useRef(false);
  const resumeAtRef = useRef(0);

  // The list is rendered twice. Drifting to the end of a finite row and
  // stopping reads as broken, and snapping back reads worse; with a second copy
  // the scroll position can be rolled back by exactly one copy's width at the
  // moment it crosses, which is invisible because the pixels either side of the
  // seam are identical.
  const loop = [...items, ...items];

  // Only where hovering is a real thing. On a touch screen a tap fires a
  // synthetic mouseenter and the matching mouseleave often never arrives —
  // frequently not until you tap something else entirely — so an indefinite
  // hover pause meant the row stopped for good the first time anybody touched
  // it. Touch gets the timed hold below instead, which always resumes.
  const canHover = typeof window !== 'undefined'
    && window.matchMedia?.('(hover: hover)').matches;

  // Timed rather than an indefinite pause: focus follows a tap on a touch
  // screen and the blur may never come, which is the same trap as hover.
  const holdOnFocus = useCallback(() => {
    resumeAtRef.current = Date.now() + RESUME_AFTER_MS * 3;
  }, []);

  const pause = useCallback(() => { pausedRef.current = true; }, []);
  const offscreenRef = useRef(false);
  const resume = useCallback(() => { pausedRef.current = false; }, []);
  const holdFor = useCallback((ms = RESUME_AFTER_MS) => {
    resumeAtRef.current = Date.now() + ms;
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;

    // Someone who has asked the system for less motion should not be given an
    // animation they cannot switch off.
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (reduced?.matches) return undefined;

    let raf = 0;
    let last = performance.now();

    // Measured here and on resize, not inside the frame. Reading scrollWidth
    // forces the browser to lay the row out, and doing that sixty times a
    // second to move it twenty pixels is most of the cost of the animation —
    // on a phone it is the difference between a drift and a hot battery.
    let half = 0;
    let scrollable = false;
    const measure = () => {
      half = el.scrollWidth / 2;
      scrollable = el.scrollWidth > el.clientWidth + 1;
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);

    const step = (now) => {
      const dt = Math.min(now - last, 100) / 1000; // a backgrounded tab returns
      last = now;                                   // one enormous delta
      const running = !pausedRef.current && !offscreenRef.current
        && !document.hidden && Date.now() >= resumeAtRef.current;
      if (running && scrollable) {
        const next = el.scrollLeft + DRIFT_PX_PER_SECOND * dt;
        el.scrollLeft = next >= half ? next - half : next;
      }
      raf = window.requestAnimationFrame(step);
    };
    raf = window.requestAnimationFrame(step);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, []);

  // Scrolled past, or in a background tab: an animation nobody can see is pure
  // cost, and on a phone that cost is battery.
  useEffect(() => {
    const el = railRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([e]) => { offscreenRef.current = !e.isIntersecting; },
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Any deliberate interaction wins over the drift, and gets a beat afterwards
  // before it starts again — otherwise the row creeps out from under the cursor
  // while someone is reading it.
  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    const onWheel = () => holdFor();
    const onTouch = () => holdFor(RESUME_AFTER_MS * 2);
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('touchstart', onTouch, { passive: true });
    el.addEventListener('touchmove', onTouch, { passive: true });
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouch);
      el.removeEventListener('touchmove', onTouch);
    };
  }, [holdFor]);

  // Scroll by a viewport rather than a fixed number of cards, so the same press
  // does the right thing on a phone and on a wide monitor.
  const page = (dir) => {
    const el = railRef.current;
    if (!el) return;
    holdFor();
    el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.85, 260), behavior: 'smooth' });
  };

  const Arrow = ({ dir, Icon, label }) => (
    <button
      type="button"
      onClick={() => page(dir)}
      aria-label={label}
      className={`hidden sm:flex absolute top-[4.4rem] -translate-y-1/2 z-20 items-center justify-center w-9 h-9 rounded-full border border-[var(--border-2)] bg-[var(--bg)]/95 text-[var(--text-2)] hover:text-[var(--text)] hover:border-[var(--text-3)] shadow-lg transition-colors ${
        dir < 0 ? 'left-0 -ml-1' : 'right-0 -mr-1'
      }`}
    >
      <Icon size={17} />
    </button>
  );

  // A 4.5rem fade off a 20rem screen swallows most of a card.
  const fade = '2rem';

  return (
    <section className="relative mt-14 sm:mt-16">
      <div className="flex items-baseline justify-between gap-4 mb-4">
        <h2 className="mono text-[0.7rem] tracking-[0.18em] uppercase text-[var(--text-3)]">
          Start from one of these
        </h2>
        <span className="text-[0.78rem] text-[var(--text-4)] hidden sm:block">
          Click one to load it above
        </span>
      </div>

      <div
        className="relative"
        onMouseEnter={canHover ? pause : undefined}
        onMouseLeave={canHover ? resume : undefined}
        {...(canHover ? {} : { onTouchStart: () => holdFor(RESUME_AFTER_MS * 2) })}
        onFocusCapture={holdOnFocus}
      >
        <Arrow dir={-1} Icon={ChevronLeft} label="Scroll left" />
        <Arrow dir={1} Icon={ChevronRight} label="Scroll right" />
        {/* The row runs to both edges of the section rather than stopping at a
            margin, so it has to say for itself that it continues. A mask does
            that without painting a gradient over the page behind it, which a
            solid overlay would have to match exactly and would get wrong on
            any background but the one it was written against. */}
        <div
          ref={railRef}
          style={{
            maskImage:
              `linear-gradient(to right, transparent 0, #000 ${fade}, #000 calc(100% - ${fade}), transparent 100%)`,
            WebkitMaskImage:
              `linear-gradient(to right, transparent 0, #000 ${fade}, #000 calc(100% - ${fade}), transparent 100%)`,
          }}
          className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1
                     [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {loop.map((item, i) => (
            <Card key={`${item.kind}:${item.id}:${i}`} item={item} onPick={onPick} />
          ))}
        </div>
      </div>
    </section>
  );
}
